import { useState, useEffect } from "react";
import { trpc } from "../../trpc";
import { Merchant } from "../../types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import { Alert } from "@/components/ui/alert";
import InternalCrmTab from "./InternalCrmTab";
import PipelineConfigTab from "./PipelineConfigTab";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  merchant: Merchant | null;
  onSuccess: () => void;
};

const EditMerchantModal = ({ isOpen, onClose, merchant, onSuccess }: Props) => {
  const [formData, setFormData] = useState<{
    billingCycleDay: number;
    minimumSubscriptionUsage: number;
    unitPriceBelowMinimum: number;
    unitPriceAboveMinimum: number;
    unitPricePerCallMinute: number;
    unitPriceOutbound: number;
    currentCustomer: boolean;
    helpCenterUrl: string;
    paymentsMerchantId: string;
    merchantIdentifierEmail: string[];
    firstInvoicePaidDate: string;
    lastInvoicePaidDate: string;
    enableContactEmployeeButton: boolean;
    calComIntegrationEnabled: boolean;
    broadcastEnabled: boolean;
    employeeNotificationsEnabled: boolean;
    observePendingLeads: boolean;
    leadExpirationDays: number;
    returningCustomerAssignmentEnabled: boolean;
    returningCustomerWindowDays: number;
    restrictEmployeeStatusChanges: boolean;
    employeeStatusSuperEmails: string[];
    leadReassignmentEnabled: boolean;
    leadReassignmentAllowedEmails: string[];
    transferNotifications: {
      internalTransfer: boolean;
      manualReassignment: boolean;
      partnerCrmReassignment: boolean;
    };
  }>({
    billingCycleDay: 1,
    minimumSubscriptionUsage: 0,
    unitPriceBelowMinimum: 0,
    unitPriceAboveMinimum: 0,
    unitPricePerCallMinute: 0,
    unitPriceOutbound: 100,
    currentCustomer: true,
    helpCenterUrl: "",
    paymentsMerchantId: "",
    merchantIdentifierEmail: [],
    firstInvoicePaidDate: "",
    lastInvoicePaidDate: "",
    enableContactEmployeeButton: false,
    calComIntegrationEnabled: false,
    broadcastEnabled: false,
    employeeNotificationsEnabled: true,
    observePendingLeads: false,
    leadExpirationDays: 7,
    returningCustomerAssignmentEnabled: false,
    returningCustomerWindowDays: 30,
    restrictEmployeeStatusChanges: false,
    employeeStatusSuperEmails: [],
    leadReassignmentEnabled: true,
    leadReassignmentAllowedEmails: [],
    transferNotifications: {
      internalTransfer: true,
      manualReassignment: true,
      partnerCrmReassignment: true,
    },
  });

  const isRentals = merchant?.type === "rentals";
  const templatesQuery = trpc.whatsappTemplates.list.useQuery(
    { merchantId: merchant?.id ?? "" },
    { enabled: isRentals && !!merchant }
  );
  const emailTemplates = (templatesQuery.data?.templates ?? []).filter(
    (t) => t.systemId?.startsWith("portals-email-outbound") || t.systemId === "outbound-default"
  );
  const templatesApproved = emailTemplates.length > 0 && emailTemplates.every((t) => t.status === "APPROVED");
  const emailsBlocked = isRentals && !templatesApproved;

  const updateMerchantMutation = trpc.merchant.updateMerchant.useMutation({
    onSuccess: () => {
      toast.success("Merchant updated successfully");
      onSuccess();
      onClose();
    },
    onError: (error) => {
      toast.error("Error updating merchant", {
        description: error.message,
      });
    },
  });

  useEffect(() => {
    if (merchant) {
      setFormData({
        billingCycleDay: merchant.billingCycleDay ?? 1,
        minimumSubscriptionUsage: merchant.minimumSubscriptionUsage ?? 0,
        unitPriceBelowMinimum: merchant.unitPriceBelowMinimum ?? 0,
        unitPriceAboveMinimum: merchant.unitPriceAboveMinimum ?? 0,
        unitPricePerCallMinute: merchant.unitPricePerCallMinute ?? 0,
        unitPriceOutbound: merchant.unitPriceOutbound ?? 100,
        currentCustomer: merchant.currentCustomer ?? true,
        helpCenterUrl: merchant.helpCenterUrl ?? "",
        paymentsMerchantId: merchant.paymentsMerchantId ?? "",
        merchantIdentifierEmail: merchant.merchantIdentifierEmail ?? [],
        firstInvoicePaidDate: merchant.firstInvoicePaidDate ?? "",
        lastInvoicePaidDate: merchant.lastInvoicePaidDate ?? "",
        enableContactEmployeeButton: merchant.enableContactEmployeeButton ?? false,
        calComIntegrationEnabled: merchant.calComIntegrationEnabled ?? false,
        broadcastEnabled: merchant.broadcastEnabled ?? false,
        employeeNotificationsEnabled: merchant.employeeNotificationsEnabled ?? true,
        observePendingLeads: merchant.observePendingLeads ?? false,
        leadExpirationDays: merchant.leadExpirationDays ?? 7,
        returningCustomerAssignmentEnabled: merchant.returningCustomerAssignmentEnabled ?? false,
        returningCustomerWindowDays: merchant.returningCustomerWindowDays ?? 30,
        restrictEmployeeStatusChanges: merchant.restrictEmployeeStatusChanges ?? false,
        employeeStatusSuperEmails: merchant.employeeStatusSuperEmails ?? [],
        // Absent ⇒ on, same default the admin getter applies.
        leadReassignmentEnabled: merchant.leadReassignmentEnabled ?? true,
        leadReassignmentAllowedEmails: merchant.leadReassignmentAllowedEmails ?? [],
        // Absent ⇒ on (effective default). Only an explicit false suppresses.
        transferNotifications: {
          internalTransfer: merchant.transferNotifications?.internalTransfer ?? true,
          manualReassignment: merchant.transferNotifications?.manualReassignment ?? true,
          partnerCrmReassignment: merchant.transferNotifications?.partnerCrmReassignment ?? true,
        },
      });
    }
  }, [merchant]);

  const handleSubmit = () => {
    if (!merchant) return;
    updateMerchantMutation.mutate({
      id: merchant.id,
      ...formData,
    });
  };

  const updateField = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateTransferNotification = (
    key: "internalTransfer" | "manualReassignment" | "partnerCrmReassignment",
    value: boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      transferNotifications: { ...prev.transferNotifications, [key]: value },
    }));
  };

  const addEmail = () => {
    setFormData((prev) => ({
      ...prev,
      merchantIdentifierEmail: [...prev.merchantIdentifierEmail, ""],
    }));
  };

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...formData.merchantIdentifierEmail];
    newEmails[index] = value;
    setFormData((prev) => ({ ...prev, merchantIdentifierEmail: newEmails }));
  };

  const removeEmail = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      merchantIdentifierEmail: prev.merchantIdentifierEmail.filter((_, i) => i !== index),
    }));
  };

  const addSuperEmail = () => {
    setFormData((prev) => ({
      ...prev,
      employeeStatusSuperEmails: [...prev.employeeStatusSuperEmails, ""],
    }));
  };

  const updateSuperEmail = (index: number, value: string) => {
    const next = [...formData.employeeStatusSuperEmails];
    next[index] = value;
    setFormData((prev) => ({ ...prev, employeeStatusSuperEmails: next }));
  };

  const removeSuperEmail = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      employeeStatusSuperEmails: prev.employeeStatusSuperEmails.filter((_, i) => i !== index),
    }));
  };

  const addReassignEmail = () => {
    setFormData((prev) => ({
      ...prev,
      leadReassignmentAllowedEmails: [...prev.leadReassignmentAllowedEmails, ""],
    }));
  };

  const updateReassignEmail = (index: number, value: string) => {
    const next = [...formData.leadReassignmentAllowedEmails];
    next[index] = value;
    setFormData((prev) => ({ ...prev, leadReassignmentAllowedEmails: next }));
  };

  const removeReassignEmail = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      leadReassignmentAllowedEmails: prev.leadReassignmentAllowedEmails.filter(
        (_, i) => i !== index
      ),
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Merchant</DialogTitle>
          <DialogDescription>
            Update merchant configuration for {merchant?.id}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="billing">Billing</TabsTrigger>
            <TabsTrigger value="emails">Emails</TabsTrigger>
            <TabsTrigger value="internalCrm">CRM Interno</TabsTrigger>
            <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="currentCustomer">Active Customer</Label>
              <Switch
                id="currentCustomer"
                checked={formData.currentCustomer}
                onCheckedChange={(checked) => updateField("currentCustomer", checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="contactEmployee">Enable Contact Employee Button</Label>
              <Switch
                id="contactEmployee"
                checked={formData.enableContactEmployeeButton}
                onCheckedChange={(checked) => updateField("enableContactEmployeeButton", checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="calComIntegrationEnabled">Cal.com Integration Enabled</Label>
              <Switch
                id="calComIntegrationEnabled"
                checked={formData.calComIntegrationEnabled}
                onCheckedChange={(checked) => updateField("calComIntegrationEnabled", checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="broadcastEnabled">Broadcast Enabled</Label>
              <Switch
                id="broadcastEnabled"
                checked={formData.broadcastEnabled}
                onCheckedChange={(checked) => updateField("broadcastEnabled", checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="employeeNotifications">Notificaciones al Asesor</Label>
              <Switch
                id="employeeNotifications"
                checked={formData.employeeNotificationsEnabled}
                onCheckedChange={(checked) => updateField("employeeNotificationsEnabled", checked)}
              />
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <p className="text-sm font-medium">Notificaciones de transferencia</p>
              <p className="text-xs text-muted-foreground">
                Controla por origen qué transferencias notifican al asesor. Requiere
                "Notificaciones al Asesor" activado.
              </p>
              <div className="flex items-center justify-between">
                <Label htmlFor="transferInternal">Transferencia interna (automática)</Label>
                <Switch
                  id="transferInternal"
                  checked={formData.transferNotifications.internalTransfer}
                  disabled={!formData.employeeNotificationsEnabled}
                  onCheckedChange={(checked) =>
                    updateTransferNotification("internalTransfer", checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="transferManual">Reasignación manual</Label>
                <Switch
                  id="transferManual"
                  checked={formData.transferNotifications.manualReassignment}
                  disabled={!formData.employeeNotificationsEnabled}
                  onCheckedChange={(checked) =>
                    updateTransferNotification("manualReassignment", checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="transferPartnerCrm">Reasignación desde CRM</Label>
                <Switch
                  id="transferPartnerCrm"
                  checked={formData.transferNotifications.partnerCrmReassignment}
                  disabled={!formData.employeeNotificationsEnabled}
                  onCheckedChange={(checked) =>
                    updateTransferNotification("partnerCrmReassignment", checked)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="helpCenterUrl">Help Center URL</Label>
              <Input
                id="helpCenterUrl"
                value={formData.helpCenterUrl}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("helpCenterUrl", e.target.value)}
                placeholder="https://..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentsMerchantId">Payments Merchant ID</Label>
              <p className="text-xs text-muted-foreground">
                Identificador de este merchant en la plataforma de pagos de Palomma. Se envía en
                los eventos que publicamos al bus de pagos. Vacío: el merchant no está conectado a
                pagos.
              </p>
              <Input
                id="paymentsMerchantId"
                value={formData.paymentsMerchantId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("paymentsMerchantId", e.target.value)}
                placeholder="mer_..."
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="observePendingLeads">Expirar Leads Pendientes</Label>
              <Switch
                id="observePendingLeads"
                checked={formData.observePendingLeads}
                onCheckedChange={(checked) => updateField("observePendingLeads", checked)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="restrictEmployeeStatusChanges">
                Cada Asesor Solo Cambia su Propio Estado
              </Label>
              <Switch
                id="restrictEmployeeStatusChanges"
                checked={formData.restrictEmployeeStatusChanges}
                onCheckedChange={(checked) => updateField("restrictEmployeeStatusChanges", checked)}
              />
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <p className="text-sm font-medium">Reasignación de leads</p>
              <p className="text-xs text-muted-foreground">
                Quién puede pasar un lead a otro asesor desde el portal. Cada asesor solo ve
                los leads asignados a él, así que en la práctica entrega los suyos.
              </p>
              <div className="flex items-center justify-between">
                <Label htmlFor="leadReassignmentEnabled">Los asesores pueden reasignar leads</Label>
                <Switch
                  id="leadReassignmentEnabled"
                  checked={formData.leadReassignmentEnabled}
                  onCheckedChange={(checked) => updateField("leadReassignmentEnabled", checked)}
                />
              </div>
              <div className="space-y-2">
                <Label>Limitar la reasignación a estos correos</Label>
                <p className="text-xs text-muted-foreground">
                  Vacío: cualquier asesor puede reasignar. Con correos: solo ellos.
                </p>
                {formData.leadReassignmentAllowedEmails.map((email, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateReassignEmail(index, e.target.value)
                      }
                      placeholder="email@example.com"
                      disabled={!formData.leadReassignmentEnabled}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeReassignEmail(index)}
                      disabled={!formData.leadReassignmentEnabled}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={addReassignEmail}
                  className="w-full"
                  disabled={!formData.leadReassignmentEnabled}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Email
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="leadExpirationDays">Dias para Expirar Lead Pendiente</Label>
              <Input
                id="leadExpirationDays"
                type="number"
                min={1}
                max={30}
                value={formData.leadExpirationDays}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField("leadExpirationDays", parseInt(e.target.value) || 1)
                }
                disabled={!formData.observePendingLeads}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="returningCustomerAssignmentEnabled">
                Asignar Cliente Recurrente a su Asesor Anterior
              </Label>
              <Switch
                id="returningCustomerAssignmentEnabled"
                checked={formData.returningCustomerAssignmentEnabled}
                onCheckedChange={(checked) =>
                  updateField("returningCustomerAssignmentEnabled", checked)
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="returningCustomerWindowDays">
                Dias para Recordar al Asesor Anterior
              </Label>
              <Input
                id="returningCustomerWindowDays"
                type="number"
                min={1}
                max={730}
                value={formData.returningCustomerWindowDays}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateField("returningCustomerWindowDays", parseInt(e.target.value) || 30)
                }
                disabled={!formData.returningCustomerAssignmentEnabled}
              />
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="billingCycleDay">Billing Cycle Day</Label>
              <Input
                id="billingCycleDay"
                type="number"
                value={formData.billingCycleDay}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("billingCycleDay", parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="minimumSubscriptionUsage">Minimum Subscription Usage (COP)</Label>
              <Input
                id="minimumSubscriptionUsage"
                type="number"
                value={formData.minimumSubscriptionUsage}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("minimumSubscriptionUsage", parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unitPriceBelowMinimum">Unit Price Below Minimum (COP)</Label>
              <Input
                id="unitPriceBelowMinimum"
                type="number"
                value={formData.unitPriceBelowMinimum}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("unitPriceBelowMinimum", parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unitPriceAboveMinimum">Unit Price Above Minimum (COP)</Label>
              <Input
                id="unitPriceAboveMinimum"
                type="number"
                value={formData.unitPriceAboveMinimum}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("unitPriceAboveMinimum", parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unitPricePerCallMinute">Unit Price Per Call Minute (COP)</Label>
              <Input
                id="unitPricePerCallMinute"
                type="number"
                value={formData.unitPricePerCallMinute}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("unitPricePerCallMinute", parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="unitPriceOutbound">Unit Price Per Outbound Template (COP)</Label>
              <Input
                id="unitPriceOutbound"
                type="number"
                value={formData.unitPriceOutbound}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("unitPriceOutbound", parseInt(e.target.value))}
              />
            </div>
          </TabsContent>

          <TabsContent value="billing" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstInvoicePaidDate">First Invoice Paid Date</Label>
              <Input
                id="firstInvoicePaidDate"
                type="date"
                value={formData.firstInvoicePaidDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("firstInvoicePaidDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastInvoicePaidDate">Last Invoice Paid Date</Label>
              <Input
                id="lastInvoicePaidDate"
                type="date"
                value={formData.lastInvoicePaidDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField("lastInvoicePaidDate", e.target.value)}
              />
            </div>
          </TabsContent>

          <TabsContent value="emails" className="space-y-4">
            {emailsBlocked && (
              <Alert status="warning">
                Debes crear y aprobar las plantillas de WhatsApp antes de configurar emails. Usa el boton "Crear plantillas" en la tarjeta del merchant.
              </Alert>
            )}
            <div className="space-y-2">
              <Label>Merchant Identifier Emails</Label>
              {formData.merchantIdentifierEmail.map((email, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEmail(index, e.target.value)}
                    placeholder="email@example.com"
                    disabled={emailsBlocked}
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeEmail(index)} disabled={emailsBlocked}>
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addEmail} className="w-full" disabled={emailsBlocked}>
                <Plus className="h-4 w-4 mr-2" />
                Add Email
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Correos que Pueden Cambiar el Estado de Cualquier Asesor</Label>
              <p className="text-xs text-muted-foreground">
                Solo aplica con "Cada Asesor Solo Cambia su Propio Estado" activado.
              </p>
              {formData.employeeStatusSuperEmails.map((email, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateSuperEmail(index, e.target.value)}
                    placeholder="email@example.com"
                  />
                  <Button variant="ghost" size="icon" onClick={() => removeSuperEmail(index)}>
                    <Minus className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addSuperEmail} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                Add Super Email
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="internalCrm" className="space-y-4">
            <InternalCrmTab merchant={merchant} />
          </TabsContent>

          <TabsContent value="pipeline" className="space-y-4">
            <PipelineConfigTab merchant={merchant} />
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={updateMerchantMutation.isLoading}>
            {updateMerchantMutation.isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditMerchantModal;
